
import { useState } from "react";
import { v4 as uuidv4 } from "uuid";
import { useToast } from "./ToastContext";
import Attachment from "../models/Attachment";


export const useAttachments = (maxFiles = Infinity) => {
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const { showToast } = useToast();

  const addFiles = (files: File[]) => {
    const existingKeys = new Set(
      attachments.map(({ file }) =>
        `${file.name}-${file.size}-${file.type}-${file.lastModified}`
      )
    );

    const newAttachments: Attachment[] = [];
    const duplicates: string[] = [];

    for (const file of files) {
      const key = `${file.name}-${file.size}-${file.type}-${file.lastModified}`;

      if (existingKeys.has(key)) {
        duplicates.push(file.name);
        continue;
      }

      existingKeys.add(key);
      newAttachments.push({ id: uuidv4(), file });
    }

    const availableSlots = Math.max(0, maxFiles - attachments.length);
    const acceptedAttachments = newAttachments.slice(0, availableSlots);
    const exceededLimit = newAttachments.length > availableSlots;

    if (duplicates.length > 0) {
      showToast(
        `Duplicate file(s) skipped: ${[...new Set(duplicates)].join(", ")}`,
        { type: "error" }
      );
    }

    if (exceededLimit) {
      showToast(
        `You can attach a maximum of ${maxFiles} file(s).`,
        { type: "error" }
      );
    }

    if (acceptedAttachments.length > 0) {
      setAttachments((prev) => [...prev, ...acceptedAttachments]);
    }
  };

  const handleFileChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    addFiles(Array.from(event.target.files || []));
    event.target.value = "";
  };

  const removeAttachment = (id: string) => {
    setAttachments((prev) =>
      prev.filter((attachment) => attachment.id !== id)
    );
  };

  const clearAttachments = () => setAttachments([]);

  return {
    attachments,
    setAttachments,
    addFiles,
    handleFileChange,
    removeAttachment,
    clearAttachments,
  };
};